import { both, defaultTo, flatten, has, map, pathOr, propOr } from 'ramda';
import { isNilOrEmpty, isNotNil, isNotNilOrEmpty, mapIndexed } from '@@serverHelpers';
import { roundToTwoDecimals } from './mathHelpers.js';

const createContributions = (healthInsurances = [], ROs = [], labelsRO = []) =>
  flatten(
    mapIndexed((insurerAmount, index) => [
      {
        code: 'InsurerAmount',
        label: defaultTo('', labelsRO[index]),
        defaultAmount: roundToTwoDecimals(defaultTo(0, parseFloat(insurerAmount)))
      },
      {
        code: 'RO',
        label: defaultTo('', labelsRO[index]),
        defaultAmount: roundToTwoDecimals(parseFloat(defaultTo(0, ROs[index])))
      }
    ], healthInsurances)
  );

const createSimpleGuaranteeContributions = (guarantees, optionCode, capCode, annualIncome) => {
  const percentage = parseFloat(pathOr(0, [capCode, 'value'], guarantees));
  const insurerAmount = roundToTwoDecimals(annualIncome * (percentage / 100));
  const healthInsurance = [insurerAmount.toString()];
  const guarantee = defaultTo(guarantees[optionCode], guarantees[capCode]);
  const ROs = propOr([], 'RO', guarantee);
  const labelsRO = propOr([], 'labelsRO', guarantee);

  return {
    healthInsurance,
    contributions: createContributions(healthInsurance, ROs, labelsRO)
  };
};

const processFranchiseCodes = (guarantees, franchiseCodes, computedFranchiseData) => {
  const updates = {};

  for (const code of franchiseCodes) {
    const franchiseData = computedFranchiseData[code];
    const hasHealthInsurances = both(
      isNotNil,
      has('healthInsurances')
    )(franchiseData);

    if (!hasHealthInsurances || isNilOrEmpty(franchiseData.healthInsurances)) continue;

    updates[code] = {
      ...guarantees[code],
      healthInsurance: franchiseData.healthInsurances,
      contributions: createContributions(
        franchiseData.healthInsurances,
        propOr([], 'ROs', franchiseData),
        propOr([], 'labelsRO', franchiseData)
      )
    };
  }

  return updates;
};

const SIMPLE_GUARANTEE_CONFIGS = [
  {
    optionCode: 'OPTION_TT_CAUSE',
    capCode: 'TT_CAUSE_CAP',
    targetCode: 'OPTION_TT_CAUSE'
  },
  {
    optionCode: 'OPTION_ACC',
    capCode: 'ACC_CAP',
    targetCode: 'OPTION_ACC'
  },
  {
    optionCode: 'OPTION_RCONJ',
    capCode: 'RCONJ_CAP',
    targetCode: 'GAR_RCONJ'
  }
];

const processSimpleGuarantees = (result, guarantees, annualIncome) => {
  for (const {
    optionCode,
    capCode,
    targetCode
  } of SIMPLE_GUARANTEE_CONFIGS) {
    if (pathOr(false, [optionCode, 'selected'], guarantees)) {
      result[targetCode] = {
        ...result[targetCode],
        ...createSimpleGuaranteeContributions(guarantees, optionCode, capCode, annualIncome)
      };
    }
  }
  return result;
};

export const calculateHealthInsurances = (guarantees, annualIncome = 0, computedFranchiseData = {}) => {
  let result = { ...guarantees };

  result = processSimpleGuarantees(result, guarantees, annualIncome);

  const reducSelected = pathOr(false, ['OPTION_REDUC', 'selected'], guarantees);
  const reducValue = pathOr('', ['REDUC_CAP', 'value'], guarantees);
  if (reducSelected && isNotNilOrEmpty(reducValue)) {
    const percentages = map(v => defaultTo(0, parseFloat(v)), reducValue.split('_'));
    const healthInsurance = map(
      percentage => roundToTwoDecimals(annualIncome * (percentage / 100)).toString(),
      percentages
    );
    const ROs = propOr([], 'RO', guarantees.REDUC_CAP);
    const labelsRO = propOr([], 'labelsRO', guarantees.REDUC_CAP);

    result.GAR_REDUC = {
      ...result.GAR_REDUC,
      healthInsurance,
      contributions: createContributions(healthInsurance, ROs, labelsRO)
    };
  }

  const invalSelected = pathOr(false, ['OPTION_INVAL', 'selected'], guarantees);
  const invalValue = pathOr('', ['INVAL_CAP', 'value'], guarantees);
  if (invalSelected && isNotNilOrEmpty(invalValue)) {
    const percentages = map(v => defaultTo(0, parseFloat(v)), invalValue.split('_'));
    const ROs = propOr([], 'RO', guarantees.OPTION_INVAL);
    const healthInsurance = mapIndexed((percentage, index) => {
      const insurerAmountBeforeRO = annualIncome * (percentage / 100);
      const insurerAmount = Math.max(0, insurerAmountBeforeRO - parseFloat(defaultTo(0, ROs[index])));
      return roundToTwoDecimals(insurerAmount).toString();
    }, percentages);
    const labelsRO = propOr([], 'labelsRO', guarantees.OPTION_INVAL);

    result.GAR_INVAL = {
      ...result.GAR_INVAL,
      healthInsurance,
      contributions: createContributions(healthInsurance, ROs, labelsRO)
    };
  }

  const incapFranchiseCodes = ['OPTION_FR_ALL', 'OPTION_FR_MAL', 'OPTION_FR_ACC', 'OPTION_FR_HOSP'];

  return {
    ...result,
    ...processFranchiseCodes(guarantees, incapFranchiseCodes, computedFranchiseData)
  };
};
